import { Validators } from '@angular/forms';
import {
  AfterViewInit,
  Component,
  inject,
  computed,
  ChangeDetectionStrategy,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { faArrowsAlt } from '@fortawesome/free-solid-svg-icons';

import { FormsService } from 'src/app/core/services/forms.service';

import {
  DialogCloseOptions,
  DialogCloseReason,
  injectDirtyCloseHandler,
} from '@sui/dialog';

import { SimpleDialogBase } from 'src/app/shared/abstract/simple-dialog-base';

import { DataObject } from 'src/app/core/models/core.models';
import { FormProperty } from 'src/app/core/models/form.models';

import { injectMoveObjectsMutation } from 'src/app/api/queries/object.queries';

import { UserWorkspacesService } from 'src/app/core/services/user-workspaces.service';

import { map } from 'rxjs';

interface DialogData {
  objects: DataObject[];
}
interface DialogOutput {}

@Component({
  selector: 'app-move-object-dialog',
  templateUrl: './move-object-dialog.component.html',
  styleUrl: './move-object-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class MoveObjectDialogComponent
  extends SimpleDialogBase<DialogData, DialogOutput>
  implements AfterViewInit
{
  private formService = inject(FormsService);
  // ! Declared above `formProperties`: that array is a field initializer and
  // ! reads this service while building the workspace picker's options.
  private userWorkspaces = inject(UserWorkspacesService);
  protected dirtyHandler = injectDirtyCloseHandler(this.dialogRef);

  private moveObjectsMutation = injectMoveObjectsMutation();

  public actionIcon = faArrowsAlt;
  public dialogTitle: string = this.translateService.instant(
    'move-object-popup.title.move-feeds',
  );

  public formProperties: FormProperty[] = [
    {
      propertyName: 'workspace_id',
      validators: [Validators.required],
      propertyLabel: this.translateService.instant(
        'move-object-popup.label.workspace',
      ),
      controlType: 'select',
      valueOptionsAsync: this.userWorkspaces.sortedWorkspaces$().pipe(
        map((workspaces) =>
          workspaces.map((workspace) => ({
            value: workspace.id,
            name: workspace.title,
          })),
        ),
      ),
    },
  ];

  protected formGroup = this.formService.createFormGroup(this.formProperties);

  private formValueChanges = toSignal(this.formGroup.valueChanges, {
    requireSync: false,
  });
  private formStatusChanges = toSignal(this.formGroup.statusChanges, {
    initialValue: this.formGroup.status,
  });

  public isDirty = computed(() => {
    this.formValueChanges();
    return this.formGroup.dirty;
  });

  public isValid = computed(() => {
    return this.formStatusChanges() === 'VALID';
  });

  get objects(): DataObject[] {
    return this.dialogData.objects;
  }

  ngAfterViewInit(): void {
    this.setupDirtyHandler(this.isDirty, this.isValid);
  }

  protected override performSave(): DialogCloseOptions<DialogOutput> {
    const workspaceId = this.formGroup.get('workspace_id')?.value;

    // * optimistic close; the hook's onSuccess owns the cache merge and broadcast
    this.moveObjectsMutation
      .mutateAsync({ objects: this.objects, workspaceId })
      .catch(() => undefined);

    return {
      reason: DialogCloseReason.Save,
      data: {},
    };
  }

  save(): void {
    if (!this.isValid()) {
      this.formGroup.markAllAsTouched();
      return;
    }

    const result = this.performSave();
    this.dirtyHandler.closeImmediately(result);
  }

  async cancel(): Promise<void> {
    await this.dirtyHandler.tryClose({
      reason: DialogCloseReason.Cancel,
    });
  }
}
